import { useQuery } from '@tanstack/react-query'
import {
  endpointPerformanceSettingsQuery,
  type EndpointIpRow,
  type EndpointPerformancePage,
} from '../api/devices'
import { CheckField, FormCaption, FormRow, HalfField } from '../ui/FormGrid'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Inventoree::deviceEndpointPerformanceSettings - formularul de citire.
 * Save / add IP / remove IP raman in settings/Inventoree (felia writes).
 *
 * ASEZAREA e cea comuna (`ui/FormGrid.tsx`), desi campurile nu se scriu inca:
 * ecranul ARATA ca un formular, cu casute si cifre, deci se citeste ca unul si
 * trebuie sa stea ca unul. Inainte, fiecare prag isi avea titlul pe doua coloane
 * din douasprezece la stanga, iar cifra intr-o cutie de trei coloane dintr-o
 * jumatate de panou - trei margini de coloana diferite intr-un singur ecran -
 * si bifa era casuta goala a browserului (13x13, albastrul lui), fiindca iCheck,
 * care o desena in portal, nu ruleaza in v4.
 *
 * Cifra isi pastreaza pastila de langa ea ("%", "Min"): `input-group` e o
 * asezare de tabel, si e scutita de regula grilei chiar in `v4.css`.
 *
 * Lista de adrese de dedesubt ramane lista: are randurile ei, nu campuri.
 *
 * CE NU S-A SCHIMBAT: ce se arata, in ce ordine, si faptul ca nimic de aici nu
 * se poate inca scrie.
 */

export function DeviceEndpointPerformanceSettings({ id }: ScreenProps) {
  const { data, error } = useQuery(endpointPerformanceSettingsQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <EndpointForm page={data} />
}

function maxOf(page: EndpointPerformancePage, key: 'processor' | 'ram' | 'swap'): number {
  return page.settings[key]?.max_value ?? 0
}

function EndpointForm({ page }: { page: EndpointPerformancePage }) {
  return (
    <div className="device-endpoint-performance-settings">
      <form id="updateEndpointAlarmForm" onSubmit={(e) => e.preventDefault()}>
        <input type="hidden" name="device_id" value={page.device.id} />

        {/*
          "Network Connection" era titlul sectiunii si ramane scris: e numele
          lucrului despre care vorbeste bifa de sub el - vezi `FormCaption`.
        */}
        <FormCaption>Network Connection</FormCaption>
        <FormRow>
          <CheckField
            id="connection_network"
            name="connection_network"
            className="icheck-radio"
            label="Enable network connection"
            checked={page.connections.connection_network === 1}
            onChange={() => undefined}
          />
        </FormRow>

        <FormRow>
          <Threshold title="Processor" name="maxValueProcessor" unit="%" value={maxOf(page, 'processor')} />
          <Threshold title="Ram" name="maxValueRam" unit="%" value={maxOf(page, 'ram')} />
        </FormRow>

        <FormRow>
          <Threshold title="Swap" name="maxValueSwap" unit="%" value={maxOf(page, 'swap')} />
          <Threshold title="Response Interval" name="response_time" unit="Min" value={page.connections.response_time} />
        </FormRow>
      </form>

      <IpList ips={page.ips} />
    </div>
  )
}

/** Un prag: cifra si unitatea ei, pe o jumatate de rand din grila. */
function Threshold({ title, name, unit, value }: { title: string; name: string; unit: string; value: number }) {
  return (
    <HalfField label={title} htmlFor={name}>
      <div className="input-group">
        <input id={name} className="form-control" name={name} type="number" value={value} readOnly />
        <span className="input-group-addon">{unit}</span>
      </div>
    </HalfField>
  )
}

function IpList({ ips }: { ips: EndpointIpRow[] }) {
  return (
    <div className="row" style={{ paddingTop: 30 }}>
      <div className="col-sm-4">
        <ul id="ip_list" style={{ border: '1px solid #d3cfcf', maxHeight: 165, overflow: 'auto' }}>
          {ips.length === 0 ? (
            <li>
              <div className="row">
                <div className="col-sm-12 align-center" style={{ backgroundColor: 'white', fontSize: 15 }}>
                  List is empty.
                </div>
              </div>
            </li>
          ) : (
            ips.map((row) => (
              <li
                key={row.id}
                className="remove_li"
                style={{ fontSize: 13, padding: '3px 15px 0px 15px', borderBottom: '1px solid #ccc', backgroundColor: 'white' }}
                value={row.id}
              >
                <div className="row">
                  <div style={{ paddingTop: 1 }} className="col-sm-4">
                    <span>{row.title}: </span>
                    {row.ip}
                  </div>
                  <div className="col-sm-3">
                    <span>Port: </span>
                    {row.port}
                  </div>
                  <div className="col-sm-3">
                    <span>Response: </span>
                    {row.max_value} Ms
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
